import React, { useEffect, useRef } from "react";
import { marked } from "marked";
import TurndownService from "turndown";

marked.setOptions({
  gfm: true,
  breaks: false,
});

const turndown = new TurndownService({
  headingStyle: "atx",
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
  emDelimiter: "*",
  strongDelimiter: "**",
});

function markdownToHtml(markdown) {
  const raw = String(markdown || "").trim();
  if (!raw) return "";
  return marked.parse(raw, { async: false });
}

function htmlToMarkdown(html) {
  const raw = String(html || "").trim();
  if (!raw || raw === "<br>" || raw === "<div><br></div>") return "";
  return turndown.turndown(raw).trim();
}

function syncEmptyState(el) {
  if (!el) return;
  const empty = !el.textContent?.trim();
  el.dataset.empty = empty ? "true" : "false";
}

/**
 * ContentEditable note editor that shows rendered markdown (no raw ** / ##)
 * while keeping the controlled value as markdown for save/generate.
 */
const NoteMarkdownEditor = ({
  value,
  onChange,
  disabled = false,
  className = "",
  minHeightClass = "min-h-[4.5rem]",
  placeholder = "Optional note",
  "aria-label": ariaLabel = "Note",
}) => {
  const editorRef = useRef(null);
  const lastEmittedRef = useRef(String(value || ""));

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = String(value || "");
    if (next === lastEmittedRef.current && el.innerHTML) {
      syncEmptyState(el);
      return;
    }
    lastEmittedRef.current = next;
    el.innerHTML = markdownToHtml(next);
    syncEmptyState(el);
  }, [value]);

  const emitChange = () => {
    const el = editorRef.current;
    if (!el || disabled) return;
    syncEmptyState(el);
    const markdown = htmlToMarkdown(el.innerHTML);
    lastEmittedRef.current = markdown;
    onChange(markdown);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData("text/plain") || "";
    document.execCommand("insertText", false, text);
  };

  return (
    <div
      ref={editorRef}
      role="textbox"
      aria-multiline="true"
      aria-label={ariaLabel}
      aria-placeholder={placeholder}
      contentEditable={!disabled}
      suppressContentEditableWarning
      onInput={emitChange}
      onBlur={emitChange}
      onPaste={handlePaste}
      data-placeholder={placeholder}
      data-empty="true"
      className={[
        "note-md-editor w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900",
        "focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)]",
        "overflow-y-auto",
        minHeightClass,
        disabled ? "bg-gray-100 cursor-not-allowed opacity-70" : "bg-white",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    />
  );
};

export default NoteMarkdownEditor;
